import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import request from "../../../Helpers/API";
import PlantillaScreen3 from "../../helpers/PlantillaScreen3";
import { CheckIcon, DownloadIcon, FileIcon, ImageIcon, PdfIcon, RejectIcon, ShieldIcon, UserIcon, XIcon } from "../Helper/Icons";
import { LoaderScreen } from "../../components/LoaderScreen";

// ── Íconos locales ────────────────────────────────────────────────────────────


// ── Config estatus ────────────────────────────────────────────────────────────
const ESTATUS_CONFIG = {
    P: { label: "Pendiente", dot: "#8d9688", bg: "#eef0ec", text: "#4f5a4b", border: "#d5d9d0" },
    J: { label: "Justificado", dot: "#0891b2", bg: "#e0f7fa", text: "#065f70", border: "#67d4e8" },
    A: { label: "Aceptado", dot: "#639922", bg: "#eaf3de", text: "#27500a", border: "#97c459" },
    X: { label: "Revisión", dot: "#d97706", bg: "#fff8e1", text: "#7c4a00", border: "#fbbf24" }, // ámbar — aceptación parcial
    R: { label: "Rechazado", dot: "#e24b4a", bg: "#fcebeb", text: "#791f1f", border: "#f09595" },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getFileIcon = (tipo) => {
    const t = tipo?.toLowerCase();
    if (["jpg", "jpeg", "png"].includes(t)) return <ImageIcon />;
    if (t === "pdf") return <PdfIcon />;
    return <FileIcon />;
};

const formatBytes = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Componente ────────────────────────────────────────────────────────────────
export const TimelineJustificacionesModal = ({
    item,       // objeto con info del conteo { id_conteo, modelo, descripcion, ... }
    onClose,
    open,
    diferencias
}) => {
    const [loading, setLoading] = useState(false);
    const [mensajes, setMensajes] = useState([]);
    const [evidencias, setEvidencias] = useState([]);
    const [loader, setLoader] = useState(false);

    // Cargar timeline al abrir
    useEffect(() => {
        if (!item?.id_conteo) return;
        setLoading(true);
        request
            .get(`AuditoriaJustificaciones/obtenerTimeline?idconteo=${item.id_conteo}`)
            .then(result => {
                setMensajes(result.mensajes ?? []);
                setEvidencias(result.evidencia ?? []);

            })
            .catch(err => Swal.fire("TIMELINE", JSON.stringify(err), "error"))
            .finally(() => setLoading(false));
    }, [item?.id_conteo]);
    if (!item) return null;

    // Agrupar evidencias por id_just para empatar con mensajes
    const evidenciasPorJust = evidencias.reduce((acc, ev) => {
        if (!acc[ev.id_just]) acc[ev.id_just] = [];
        acc[ev.id_just].push(ev);
        return acc;
    }, {});

    // Descargar archivo
    const descargarArchivo = (rutaServidor, nombreArchivo, tipoArchivo) => {
        setLoader(true);
        request
            .getBlob(`AuditoriaJustificaciones/DescargarArchivos?ruta=${rutaServidor}&nombre=${nombreArchivo}&extension=${tipoArchivo}`)
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = nombreArchivo;
                link.click();
                URL.revokeObjectURL(url);
            })
            .catch((error) => {
                Swal.fire("JUSTIFICACIONES", JSON.stringify(error), "error");
            })
            .finally(() => setLoader(false));

    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose?.();
    };

    return (
        <div className="jd-backdrop" onClick={handleBackdropClick}>
            <PlantillaScreen3
                titulo={"Línea de tiempo"}
                className={"jd-modal ram-modal tl-modal scroll-4"}
            >
                <button className="ram-close" onClick={onClose} type="button">X</button>

                <LoaderScreen loader={loading} />

                {/* Subheader con info del conteo */}
                <div className="jd-modal__subheader">
                    <span className="jd-modal__eyebrow">
                        {item.modelo} — {item.descripcion}
                    </span>
                    <div className="tl-diff-strip">
                        <span className="tl-diff-item">
                            <span className="tl-diff-item__label">Sistema</span>
                            <span className="tl-diff-item__value">{item.cant_sistema}</span>
                        </span>
                        <span className="tl-diff-item">
                            <span className="tl-diff-item__label">Físico</span>
                            <span className="tl-diff-item__value">{item.cant_fisica}</span>
                        </span>
                        <span className="tl-diff-item tl-diff-item--diff">
                            <span className="tl-diff-item__label">Diferencia</span>
                            <span className="tl-diff-item__value">
                                {diferencias > 0 ? `+${diferencias}` : diferencias}
                            </span>
                        </span>
                    </div>
                </div>

                {/* ── Timeline ── */}
                <div className="tl-container">
                    {!loading && mensajes.length === 0 && (
                        <div className="tl-empty">Sin mensajes en la línea de tiempo.</div>
                    )}

                    {mensajes.map((msg, idx) => {
                        const archivosMsg = evidenciasPorJust[msg.idj_ust] ?? [];
                        const estatusCfg = ESTATUS_CONFIG[msg.estatus] ?? ESTATUS_CONFIG["P"];
                        const tieneRespuesta = msg.tipo_actor === "AUDITOR";
                        const esParcial = msg.estatus === "X"; // ← aceptación parcial

                        return (
                            <div key={idx} className="tl-entry">

                                {/* ── Mensaje del almacenista (izquierda) — NO mostrar si es parcial ── */}
                                {!esParcial && (
                                    <div className="tl-row tl-row--left">
                                        <div className="tl-avatar tl-avatar--almacen">
                                            <UserIcon />
                                        </div>
                                        <div className="tl-bubble tl-bubble--almacen">
                                            <div className="tl-bubble__header">
                                                <span className="tl-bubble__nombre">{msg.usuario}</span>
                                                <span className="tl-bubble__fecha">{msg.fecha_envio}</span>
                                                <span
                                                    className="tl-bubble__badge"
                                                    style={{ background: estatusCfg.bg, color: estatusCfg.text, borderColor: estatusCfg.border }}
                                                >
                                                    {estatusCfg.label}
                                                </span>
                                            </div>

                                            {msg.comentario_usuario && (
                                                <p className="tl-bubble__texto">{msg.comentario_usuario}</p>
                                            )}

                                            {archivosMsg.length > 0 && (
                                                <div className="tl-archivos">
                                                    {archivosMsg.map((arch, ai) => (
                                                        <div
                                                            key={ai}
                                                            className="tl-archivo-chip"
                                                            onClick={() => descargarArchivo(arch.ruta_servidor, arch.nombre_archivo, arch.tipo_archivo)}
                                                            title="Descargar archivo"
                                                        >
                                                            {getFileIcon(arch.tipo_archivo)}
                                                            <span className="tl-archivo-chip__nombre">{arch.nombre_archivo}</span>
                                                            <span className="tl-archivo-chip__size">{formatBytes(arch.tamanio_bytes)}</span>
                                                            <span className="tl-archivo-chip__dl"><DownloadIcon /></span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ── Respuesta del auditor (derecha) ── */}
                                {/* Mostrar si tipo_actor es AUDITOR, o si es aceptación parcial (X) */}
                                {(tieneRespuesta || esParcial) && (
                                    <div className="tl-row tl-row--right">
                                        <div className={`tl-bubble tl-bubble--auditor ${msg.estatus === "R" ? "tl-bubble--rechazado" :
                                            msg.estatus === "X" ? "tl-bubble--parcial" :
                                                "tl-bubble--aceptado"
                                            }`}>
                                            <div className="tl-bubble__header">
                                                <span className="tl-bubble__fecha">{msg.fecha_revision}</span>
                                                <span className="tl-bubble__nombre">{msg.auditor}</span>
                                            </div>

                                            <div className="tl-bubble__resolucion">
                                                {msg.estatus === "A" && (
                                                    <span className="tl-resolucion tl-resolucion--aceptado">
                                                        <CheckIcon /> Justificación aceptada
                                                    </span>
                                                )}
                                                {msg.estatus === "X" && (
                                                    <span className="tl-resolucion tl-resolucion--parcial">
                                                        <CheckIcon /> Aceptación parcial — {msg.aceptadas} de {Math.abs(diferencias ?? 0)}
                                                    </span>
                                                )}
                                                {msg.estatus === "R" && (
                                                    <span className="tl-resolucion tl-resolucion--rechazado">
                                                        <RejectIcon /> Justificación rechazada
                                                    </span>
                                                )}
                                            </div>

                                            {msg.estatus === "R" && msg.motivo_rechazo && (
                                                <p className="tl-bubble__texto tl-bubble__texto--rechazo">
                                                    {msg.motivo_rechazo}
                                                </p>
                                            )}
                                        </div>
                                        <div className={`tl-avatar ${msg.estatus === "X" ? "tl-avatar--parcial" : "tl-avatar--auditor"}`}>
                                            <ShieldIcon />
                                        </div>
                                    </div>
                                )}

                            </div>
                        );
                    })}
                </div>

            </PlantillaScreen3>
        </div>
    );
};

export default TimelineJustificacionesModal;
